import { IOnboardingUSDBankRequest } from '@/@types/Onboarding';
import CustomSelect from '@/components/shared/CustomSelect/CustomSelect';
import { Input } from '@/components/shared/Input/Input';
import { Button } from '@/components/ui/button';
import { CountryDropdown } from '@/components/ui/country-dropdown';
import { Label } from '@/components/ui/label';
import { SheetFooter } from '@/components/ui/sheet';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/hooks/use-toast';
import { OnboardingService } from '@/services/onboarding.service';
import { ProfileService } from '@/services/profile.service';
import { AuthService } from '@/services/auth.service';
import { useAuth } from '@/auth/Auth';
import useModalStore from '@/store/useModalStore';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { SecurityChallengeModal } from '@/components/shared/SecurityChallenge';
import {
  formatApiFieldError,
  isChallengeTokenError,
  isOnboardingStepFinished,
} from '@/hooks/useSecurityChallenge';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ZodType } from 'zod';

interface BankAccountFormProps {
  mode: 'create' | 'edit';
  bank?: any;
  rail: 'WIRE' | 'SWIFT' | 'ACH' | 'VIRTUAL';
  requiredFields: string[];
  schema: ZodType<any>;
  canEdit?: boolean;
  isOnboarding?: boolean;
}

export const BankAccountForm: React.FC<BankAccountFormProps> = ({
  mode,
  bank,
  rail,
  requiredFields,
  schema,
  canEdit = true,
  isOnboarding = false,
}) => {
  const { t } = useTranslation(['onboarding', 'common']);
  const { authData } = useAuth();
  const { closeModal } = useModalStore();
  const queryClient = useQueryClient();

  const { data: userInfo } = useQuery({
    queryKey: ['user-info'],
    queryFn: async () => await AuthService.userInfo(),
    staleTime: 0,
  });

  const {
    register,
    handleSubmit,
    setValue,
    setError,

    formState: { errors, isDirty },
    watch,
    reset,
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: bank,
  });

  const lastHydratedKeyRef = useRef<string | null>(null);
  const previousUserIdRef = useRef<string | number | undefined>(undefined);

  useEffect(() => {
    const userId = authData?.id;
    if (previousUserIdRef.current === userId) return;
    previousUserIdRef.current = userId;
    lastHydratedKeyRef.current = null;
    reset({});
  }, [authData?.id, reset]);

  useEffect(() => {
    if (mode !== 'edit' || !bank?.id) return;
    const key = `${String(authData?.id ?? '')}:${String(bank.id)}:${rail}`;
    if (lastHydratedKeyRef.current === key) return;
    lastHydratedKeyRef.current = key;
    reset(bank);
  }, [mode, bank, rail, reset, authData?.id]);

  const onClose = () => {
    closeModal('bank');
  };

  const defaultValues = watch();

  const [isChallengeOpen, setIsChallengeOpen] = useState(false);
  const [pendingData, setPendingData] =
    useState<IOnboardingUSDBankRequest | null>(null);
  const pendingDataRef = useRef<IOnboardingUSDBankRequest | null>(null);

  const needsChallenge =
    !isOnboarding &&
    mode === 'edit' &&
    (isOnboardingStepFinished(userInfo?.onboarding_step) ||
      isOnboardingStepFinished(authData?.onboarding_step));

  const mutationFn = ({
    data,
    challengeToken,
  }: {
    data: IOnboardingUSDBankRequest;
    challengeToken?: string;
  }) => {
    if (isOnboarding) {
      return OnboardingService.createInternationalBankAccount(data);
    }
    if (mode === 'create') {
      return OnboardingService.createInternationalBankAccount(data);
    }
    return ProfileService.updateInternationalBankAccounts({
      data,
      accountId: bank.id,
      challengeToken,
    });
  };



  const { mutate, isPending: isSubmitting } = useMutation({
    mutationFn: mutationFn as any,
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['user-info'],
      });
      await queryClient.invalidateQueries({
        queryKey: ['list-usd-banks'],
      });
      await queryClient.invalidateQueries({
        queryKey: ['my-account', 'usd-banks'],
      });
      toast({ description: t('common:data_saved'), variant: 'success' });
      onClose();
    },
    onError: (err: any) => {
      if (isChallengeTokenError(err)) {
        setIsChallengeOpen(true);
        return;
      }
      if (err?.response?.data?.errors) {
        Object.entries(err.response.data.errors).forEach(([key, message]) => {
          if (key === 'challenge_token') return;
          setError(key as keyof IOnboardingUSDBankRequest, {
            type: 'manual',
            message: formatApiFieldError(message as string | string[]),
          });
        });
      }
    },
  });

  const onSubmit = (data: IOnboardingUSDBankRequest, challengeToken?: string) => {
    if (needsChallenge && !challengeToken) {
      setIsChallengeOpen(true);
      return;
    }
    mutate({ data, challengeToken } as any);
  };

  const handleGatedSubmit = (data: IOnboardingUSDBankRequest) => {
    pendingDataRef.current = data;
    setPendingData(data);
    if (needsChallenge) {
      setIsChallengeOpen(true);
      return;
    }
    onSubmit(data);
  };

  const handleClose = () => closeModal('bank');

  return (
    <>
    <div className='space-y-4'>
      {requiredFields.includes('provider') && (
        <CustomSelect
          placeholder={t('select_provider')}
          value={defaultValues?.provider}
          label={t('provider')}
          options={[
            { label: 'Payoneer', value: 'payoneer' },
            { label: 'Mercury', value: 'mercury' },
            { label: 'Revolut', value: 'revolut' },
            { label: 'Wise', value: 'wise' },
          ]}
          onValueChange={(value) => setValue('provider', value)}
          className='mb-2'
          disabled={!canEdit}
        />
      )}
      {requiredFields.includes('virtual_account_id') && (
        <Input
          label={t('virtual_account_id')}
          placeholder='t411511515'
          className='mb-2'
          {...register('virtual_account_id')}
          hasError={!!errors.virtual_account_id}
          helperText={String(errors.virtual_account_id?.message || '')}
          disabled={!canEdit}
        />
      )}
      {requiredFields.includes('payoneer_email') && defaultValues?.provider === 'payoneer' && (
        <Input
          label={t('payoneer_email')}
          placeholder='email@payoneer.com'
          type='email'
          className='mb-2'
          {...register('payoneer_email')}
          hasError={!!errors.payoneer_email}
          helperText={String(errors.payoneer_email?.message || '')}
          disabled={!canEdit}
        />
      )}
      {requiredFields.includes('swift_code') && (
        <Input
          label={t('swift_code')}
          placeholder='BOFAUS3NXXX'
          className='mb-2'
          {...register('swift_code')}
          hasError={!!errors.swift_code}
          helperText={String(errors.swift_code?.message || '')}
          disabled={!canEdit}
        />
      )}
      {requiredFields.includes('iban') && (
        <Input
          label={t('iban')}
          placeholder='GB33BUKB20201555555555'
          className='mb-2'
          {...register('iban')}
          hasError={!!errors.iban}
          helperText={String(errors.iban?.message || '')}
          disabled={!canEdit}
        />
      )}
      {requiredFields.includes('account_holder_name') && (
        <Input
          label={t('account_holder_name')}
          placeholder={t('account_holder_name_placeholder')}
          className='mb-2'
          {...register('account_holder_name', { required: 'Required' })}
          hasError={!!errors.account_holder_name}
          helperText={String(errors.account_holder_name?.message || '')}
          disabled={!canEdit}
        />
      )}
      {requiredFields.includes('account_holder_type') && (
        <CustomSelect
          placeholder={t('select_account_holder_type')}
          defaultValue='INDIVIDUAL'
          value={defaultValues?.account_holder_type}
          label={t('account_holder_type')}
          options={[
            { label: t('individual'), value: 'INDIVIDUAL' },
            { label: t('business'), value: 'BUSINESS' },
          ]}
          onValueChange={(value) => setValue('account_holder_type', value)}
          className='mb-2'
          disabled={!canEdit}
        />
      )}
      {requiredFields.includes('routing_number') && (
        <Input
          label={t('routing_number')}
          placeholder='021000021'
          className='mb-2'
          {...register('routing_number', { required: 'Required' })}
          hasError={!!errors.routing_number}
          helperText={String(errors.routing_number?.message || '')}
          disabled={!canEdit}
        />
      )}
      {requiredFields.includes('account_number') && (
        <Input
          label={t('account_number')}
          placeholder={t('account_number_placeholder')}
          className='mb-2'
          inputMode='numeric'
          pattern='[0-9]*'
          {...register('account_number', {
            required: 'Required',
            onChange: (e) => {
              const next =
                typeof e?.target?.value === 'string'
                  ? e.target.value.replace(/\D/g, '')
                  : e?.target?.value;
              if (typeof next === 'string' && next !== e.target.value) {
                e.target.value = next;
              }
            },
            setValueAs: (v) =>
              typeof v === 'string' ? v.replace(/\D/g, '') : v,
          })}
          onPaste={(e) => {
            const text = e.clipboardData?.getData('text') ?? '';
            const digits = text.replace(/\D/g, '');
            if (digits !== text) {
              e.preventDefault();
              document.execCommand('insertText', false, digits);
            }
          }}
          hasError={!!errors.account_number}
          helperText={String(errors.account_number?.message || '')}
          disabled={!canEdit}
        />
      )}
      {requiredFields.includes('account_class') && (
        <CustomSelect
          placeholder=''
          value={defaultValues?.account_class}
          label={t('account_class')}
          options={[
            { label: 'Checking', value: 'CHECKING' },
            { label: 'Savings', value: 'SAVINGS' },
          ]}
          onValueChange={(value) => setValue('account_class', value)}
          className='mb-2'
          disabled={!canEdit}
        />
      )}
      {requiredFields.includes('bank_name') && (
        <Input
          label={t('bank_name')}
          placeholder={t('bank_name_placeholder')}
          className='mb-2'
          {...register('bank_name')}
          hasError={!!errors.bank_name}
          helperText={String(errors.bank_name?.message || '')}
          disabled={!canEdit}
        />
      )}
      {requiredFields.includes('bank_country') && (
        <div>
          <Label htmlFor='country-dropdown' className='mb-2 block'>
            {t('bank_country')}
          </Label>
          <CountryDropdown
            onChange={(item) => setValue('bank_country', item.alpha2)}
            defaultValue={defaultValues?.bank_country || 'US'}
            placeholder='Select country'
            data-cy='country-dropdown'
            disabled={!canEdit}
          />
        </div>
      )}
      {requiredFields.includes('bank_address') && (
        <div className='grid w-full items-center gap-2'>
          <Label className='!text-white'>{t('bank_address_label')}</Label>
          <Textarea
            placeholder={t('bank_address_placeholder')}
            className='min-h-[96px]'
            {...register('bank_address')}
            disabled={!canEdit}
          />
          {errors.bank_address?.message && (
            <p className='text-sm text-error'>
              {String(errors.bank_address?.message || '')}
            </p>
          )}
        </div>
      )}

      <SheetFooter className='flex gap-2 border-t border-t-colorBorder p-4 max-md:pb-24 justify-end'>
        <Button type='button' variant='ghost' onClick={handleClose}>
          {t('cancel')}
        </Button>
        <Button
          onClick={handleSubmit(handleGatedSubmit)}
          type='button'
          disabled={
            !canEdit || isSubmitting || (mode === 'edit' && !isDirty)
          }
        >
          {isSubmitting ? (
            <span className='flex items-center gap-2'>
              <Loader2 className='animate-spin' />
              {t('saving')}
            </span>
          ) : (
            t('save')
          )}
        </Button>
      </SheetFooter>
    </div>

    <SecurityChallengeModal
      open={isChallengeOpen}
      purpose='bank_account_update'
      email={userInfo?.email ?? authData?.email}
      metadata={{ currency: 'USD', usd_bank_account_uuid: bank?.id }}
      onVerified={(token) => {
        const payload = pendingDataRef.current ?? pendingData;
        setIsChallengeOpen(false);
        if (payload && token) {
          onSubmit(payload, token);
        }
      }}
      onCancel={() => setIsChallengeOpen(false)}
    />
    </>
  );
};